/*
 * @Copyright 2024-2025. Institute for Future Intelligence, Inc.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AutoComplete, Button, Col, Input, Modal, Row, Select } from 'antd';
import Draggable, { DraggableBounds, DraggableData, DraggableEvent } from 'react-draggable';
import { useStore } from '../../stores/common';
import * as Selector from '../../stores/selector';
import { usePrimitiveStore } from '../../stores/commonPrimitive';
import { ProjectType, REGEX_ALLOWABLE_IN_NAME } from '../../constants';
import { useTranslation } from 'react-i18next';
import { readProjectIndex } from '../../cloudProjectUtil';

const { Option } = Select;

const NewProjectDialog = React.memo(({ saveAs }: { saveAs: boolean }) => {
  const loggable = useStore(Selector.loggable);
  const logAction = useStore(Selector.logAction);
  const language = useStore(Selector.language);

  const [projectType, setProjectType] = useState<ProjectType>(
    useStore.getState().projectState.type ?? ProjectType.DRUG_DISCOVERY,
  );
  // Save As starts from the current title and description; a NEW project starts
  // blank — pre-filling the open project's title invited saving over it.
  const [projectTitle, setProjectTitle] = useState<string | null>(
    saveAs ? useStore.getState().projectState.title : null,
  );
  const [projectDescription, setProjectDescription] = useState<string | null>(
    saveAs ? useStore.getState().projectState.description : null,
  );
  const [dragEnabled, setDragEnabled] = useState<boolean>(false);
  const [bounds, setBounds] = useState<DraggableBounds>({ left: 0, top: 0, bottom: 0, right: 0 } as DraggableBounds);
  const dragRef = useRef<HTMLDivElement | null>(null);

  // The user's existing project titles, fetched once when the dialog opens.
  // They feed the Title field's autocomplete: as the user types, matching
  // existing titles drop down — a match tells them the name is taken and they
  // need a different one (both Create New Project and Save As reject a title
  // that's already used). Read from the compact projectIndex map — a single
  // small document read.
  const [existingTitles, setExistingTitles] = useState<string[] | null>(null);
  useEffect(() => {
    const uid = useStore.getState().user.uid;
    if (!uid) {
      setExistingTitles([]);
      return;
    }
    let cancelled = false;
    readProjectIndex(uid)
      .then((list) => {
        if (!cancelled) setExistingTitles((list ?? []).map((p) => p.title).sort((a, b) => a.localeCompare(b)));
      })
      .catch(() => {
        if (!cancelled) setExistingTitles([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // A project's Firestore doc id IS its title, so the collision check is on the
  // trimmed title, matching saveProjectAs / createNewProject.
  const trimmedTitle = (projectTitle ?? '').trim();
  const titleCollision = existingTitles !== null && trimmedTitle.length > 0 && existingTitles.includes(trimmedTitle);

  const { TextArea } = Input;

  const { t } = useTranslation();
  const lang = useMemo(() => {
    return { lng: language };
  }, [language]);

  const onStart = (event: DraggableEvent, uiData: DraggableData) => {
    if (dragRef.current) {
      const { clientWidth, clientHeight } = window.document.documentElement;
      const targetRect = dragRef.current.getBoundingClientRect();
      setBounds({
        left: -targetRect.left + uiData.x,
        right: clientWidth - (targetRect.right - uiData.x),
        top: -targetRect.top + uiData.y,
        bottom: clientHeight - (targetRect?.bottom - uiData.y),
      });
    }
  };

  const onCancelClick = () => {
    if (saveAs) {
      usePrimitiveStore.getState().setSaveProjectAsDialog(false);
    } else {
      usePrimitiveStore.getState().setCreateProjectDialog(false);
    }
  };

  const onOkClick = () => {
    usePrimitiveStore.getState().set((state) => {
      if (saveAs) {
        state.saveProjectAsFlag = true;
        state.saveProjectAsDialog = false;
      } else {
        state.createProjectFlag = true;
        state.createProjectDialog = false;
      }
      state.projectType = projectType;
      state.projectTitle = projectTitle;
      state.projectDescription = projectDescription;
    });
    if (loggable) logAction(saveAs ? 'Save Project as' : 'Create New Project');
  };

  return (
    <Modal
      width={560}
      open={true}
      title={
        <div
          style={{ width: '100%', cursor: 'move' }}
          onMouseOver={() => setDragEnabled(true)}
          onMouseOut={() => setDragEnabled(false)}
        >
          {`${t(saveAs ? 'menu.project.SaveProjectAs' : 'menu.project.CreateNewProject', lang)}`}
        </div>
      }
      footer={[
        <Button key="Cancel" onClick={onCancelClick}>
          {`${t('word.Cancel', lang)}`}
        </Button>,
        <Button key="OK" type="primary" onClick={onOkClick} disabled={!trimmedTitle || titleCollision}>
          {`${t('word.OK', lang)}`}
        </Button>,
      ]}
      // this must be specified for the x button in the upper-right corner to work
      onCancel={() => {
        if (saveAs) {
          usePrimitiveStore.getState().setSaveProjectAsDialog(false);
        } else {
          usePrimitiveStore.getState().setCreateProjectDialog(false);
        }
      }}
      maskClosable={false}
      destroyOnClose={false}
      modalRender={(modal) => (
        <Draggable
          nodeRef={dragRef}
          disabled={!dragEnabled}
          bounds={bounds}
          onStart={(event, uiData) => onStart(event, uiData)}
        >
          <div ref={dragRef}>{modal}</div>
        </Draggable>
      )}
    >
      <Row gutter={6} style={{ paddingBottom: '4px' }}>
        <Col className="gutter-row" span={8}>
          {t('projectPanel.ProjectType', lang) + ':'}
        </Col>
        <Col className="gutter-row" span={16}>
          <Select
            disabled={saveAs}
            style={{ width: '100%' }}
            value={projectType}
            onChange={(value) => {
              setProjectType(value);
            }}
          >
            <Option key={ProjectType.MOLECULAR_MODELING} value={ProjectType.MOLECULAR_MODELING}>
              {`${t('term.MolecularModeling', lang)}`}
            </Option>
            <Option key={ProjectType.DRUG_DISCOVERY} value={ProjectType.DRUG_DISCOVERY}>
              {`${t('term.DrugDiscovery', lang)}`}
            </Option>
            <Option key={ProjectType.CRYSTAL_BUILDER} value={ProjectType.CRYSTAL_BUILDER}>
              {`${t('term.CrystalBuilder', lang)}`}
            </Option>
            <Option key={ProjectType.POLYMER_BUILDER} value={ProjectType.POLYMER_BUILDER}>
              {`${t('term.PolymerBuilder', lang)}`}
            </Option>
            <Option key={ProjectType.SEMICONDUCTOR_DESIGN} value={ProjectType.SEMICONDUCTOR_DESIGN}>
              {`${t('term.SemiconductorDesign', lang)}`}
            </Option>
            <Option key={ProjectType.NANOMATERIAL_DESIGN} value={ProjectType.NANOMATERIAL_DESIGN}>
              {`${t('term.NanomaterialDesign', lang)}`}
            </Option>
            <Option key={ProjectType.PROTEIN_LAB} value={ProjectType.PROTEIN_LAB}>
              {`${t('term.ProteinLab', lang)}`}
            </Option>
            <Option key={ProjectType.DNA_RNA_LAB} value={ProjectType.DNA_RNA_LAB}>
              {`${t('term.DnaRnaLab', lang)}`}
            </Option>
          </Select>
        </Col>
      </Row>

      <Row gutter={6} style={{ paddingBottom: '4px' }}>
        <Col className="gutter-row" span={8}>
          {`${t('word.Title', lang)}`}:
        </Col>
        <Col className="gutter-row" span={16}>
          {/* Autocomplete over the user's existing project titles: typing shows
              matching names, so a collision is obvious before OK. */}
          <AutoComplete
            style={{ width: '100%' }}
            value={projectTitle ?? ''}
            options={(existingTitles ?? []).map((title) => ({ value: title }))}
            filterOption={(inputValue, option) =>
              (option?.value ?? '').toUpperCase().includes(inputValue.toUpperCase())
            }
            onChange={(value) => setProjectTitle(value)}
          >
            <Input
              maxLength={50}
              status={titleCollision ? 'error' : undefined}
              onKeyDown={(e) => {
                if (!REGEX_ALLOWABLE_IN_NAME.test(e.key)) {
                  e.preventDefault();
                  return false;
                }
                e.stopPropagation(); // do this to prevent this event from passing to the canvas behind it
              }}
            />
          </AutoComplete>
          {titleCollision && (
            <div style={{ color: '#cf1322', fontSize: '11px', paddingTop: '2px' }}>
              {`${t('projectPanel.TitleAlreadyExists', lang)}`}
            </div>
          )}
        </Col>
      </Row>

      <Row gutter={6} style={{ paddingBottom: '4px' }}>
        <Col className="gutter-row" span={8}>
          {`${t('word.Description', lang)}`}:<br />
          <span style={{ fontSize: '10px' }}>({`${t('word.MaximumCharacters', lang)}`}: 200)</span>
        </Col>
        <Col className="gutter-row" span={16}>
          <TextArea
            rows={5}
            maxLength={200}
            style={{ width: '100%' }}
            value={projectDescription ?? ''}
            onKeyDown={(e) => {
              e.stopPropagation(); // do this to prevent this event from passing to the canvas behind it
            }}
            onChange={(e) => {
              setProjectDescription(e.target.value);
            }}
          />
        </Col>
      </Row>
    </Modal>
  );
});

export default NewProjectDialog;
